import React, { useState, useEffect, useRef, useMemo } from "react";
import { ArrowDown } from "lucide-react";
import { motion } from "framer-motion";
import {
  SiPytorch,
  SiHuggingface,
  SiLangchain,
  SiDatabricks,
  SiMlflow,
} from "react-icons/si";
import "../css/Home.css";
import Footer from "../Components/Footer";
import Navbar from "../Components/Navbar";
function Home() {
  document.title = "Harsh Amin | Home";
  const lines = useMemo(
    () => [
      " ",
      "Machine Learning Engineer",
      "AI Engineer",
      "Software Developer",
      "Cloud Solutions Architect",
      "Innovative Problem Solver",
    ],
    [],
  );

  const languages = [
    { name: "JavaScript", color: "rgba(255, 223, 0, 0.03)" },
    { name: "TypeScript", color: "rgba(0, 122, 204, 0.03)" },
    { name: "Python", color: "rgba(0, 123, 255, 0.03)" },
    { name: "Java", color: "rgba(245, 127, 23, 0.03)" },
    { name: "C/C++", color: "rgba(0, 86, 156, 0.03)" },
    { name: "HTML", color: "rgba(255, 87, 34, 0.03)" },
    { name: "CSS", color: "rgba(38, 38, 215, 0.03)" },
    { name: "R", color: "rgba(255, 0, 0, 0.03)" },
    { name: "Racket", color: "rgba(44, 160, 44, 0.03)" },
  ];

  const webDevFrameworks = [
    "React.js",
    "Node.js",
    "Express.js",
    "Next.js",
    "Flask",
    "FastAPI",
  ];
  // Icon entries use react-icons; the rest load /skillsimgs/<name>.png
  const mlframeworks = [
    { name: "PyTorch", Icon: SiPytorch, color: "#EE4C2C" },
    { name: "Hugging Face", Icon: SiHuggingface, color: "#FF9D00" },
    { name: "LangChain", Icon: SiLangchain, color: "#1C3C3C" },
    { name: "Databricks", Icon: SiDatabricks, color: "#FF3621" },
    { name: "MLflow", Icon: SiMlflow, color: "#0194E2" },
    { name: "Scikit-learn" },
    { name: "Pandas" },
    { name: "NumPy" },
    { name: "OpenCV" },
  ];
  const testingframeworks = [
    "JUnit",
    "Mocha.js",
    "Chai.js",
    "PyTest",
    "Selenium",
  ];
  const tools = [
    "AWS",
    "PostgreSQL",
    "MongoDB",
    "Postman",
    "Git",
    "JupyterLab",
    "Docker",
    "SQLAlchemy",
    "Linux/Unix",
  ];
  const [currentLine, setCurrentLine] = useState("");
  const [index, setIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [charIndex, setCharIndex] = useState(0);
  const [speed, setSpeed] = useState(100); // Typing/Deleting speed
  const About = useRef(null);

  const scrollDown = () => {
    About.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const typeTimeout = setTimeout(() => {
      const currentTitle = lines[index];
      if (!isDeleting) {
        // Typing
        setCurrentLine(currentTitle.substring(0, charIndex + 1));
        setCharIndex((prev) => prev + 1);

        if (charIndex + 1 === currentTitle.length) {
          // Pause before deleting
          setIsDeleting(true);
          setSpeed(1000);
        } else {
          setSpeed(75);
        }
      } else {
        // Deleting
        setCurrentLine(currentTitle.substring(0, charIndex - 1));
        setCharIndex((prev) => prev - 1);

        if (charIndex - 1 === 0) {
          // Move to next title
          setIsDeleting(false);
          setIndex((prev) => (prev + 1) % lines.length); // Loop through titles
          setSpeed(200);
        } else {
          setSpeed(75);
        }
      }
    }, speed);

    return () => clearTimeout(typeTimeout); // Clean up timeout
  }, [charIndex, isDeleting, speed, index, lines]);

  return (
    <div className="home-wrapper min-h-screen flex flex-col flex-grow">
      <Navbar />
      <div className="body2 flex overflow-hidden h-screen px-4">
        <div className="textWrap text-center sm:text-left mt-[20vh] sm:mt-[25vh] md:mt-[30vh] text-[#333] w-full sm:w-auto sm:ml-[5vw] md:ml-[7vw]">
          <motion.div
            className="hamin font-semibold text-[3rem] sm:text-[4rem] md:text-[5rem] lg:text-[7rem] text-[#062930]"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            Hi I'm <span className="gold-text">Harsh Amin</span>
          </motion.div>
          <motion.h2
            className="text-[2rem] sm:text-[2rem] md:text-[3rem] lg:text-[4rem] tracking-[0.5px] sm:tracking-[1px] font-extrabold text-[#062930]"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            I'm a <span className="gold-text">{currentLine}</span>
            <div className="cursor h-[30px] sm:h-[30px] md:h-[40px] lg:h-[50px]"></div>
          </motion.h2>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.45, duration: 0.5 }}
          className="absolute bottom-10 flex justify-center w-[95vw] text-center"
        >
          <button
            onClick={scrollDown}
            className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
          >
            <span className="text-gray-700 text-opacity-50">
              Scroll to explore
            </span>
            <ArrowDown color="#374151" className="animate-bounce" />
          </button>
        </motion.div>
      </div>

      <motion.h2
        ref={About}
        className="about-header"
        whileInView={{ opacity: 1, y: 0 }}
        initial={{ opacity: 0, y: 50 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        About Me
      </motion.h2>

      <div className="about-me flex flex-col md:flex-row min-h-screen justify-center items-center gap-[10vw]">
        <motion.div
          className="about-image md:w-[40%] flex justify-center items-center"
          whileInView={{ opacity: 1, x: 0 }}
          initial={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <motion.div
            className="w-[200px] h-[350px] md:w-[350px] md:h-[600px] rounded-full overflow-hidden border-4 border-white shadow-[0px_10px_50px_rgba(225,225,225,0.7)] ring-4 ring-white/30 hover:ring-8 hover:ring-white/50 transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <img
              className="w-full h-full object-cover brightness-125 shadow-lg"
              src="/harsh.png"
              alt="Harsh"
            />
          </motion.div>
        </motion.div>

        <motion.p
          className="about-text w-[85%] md:w-[40%] text-[#2b2b2b] text-[0.95rem] lg:text-[1.05rem] leading-relaxed"
          whileInView={{ opacity: 1, x: 0 }}
          initial={{ opacity: 0, x: 50 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
        >
          I'm a Computer Science and Mathematics student at the University of
          British Columbia focused on machine learning and AI systems. I like
          building things that make it all the way to production, from the model
          and the data pipeline to the cloud infrastructure it runs on.
          <br />
          <br />
          This past summer I was a Machine Learning Engineer Intern on Amazon's
          AI Center of Excellence, where I built evaluation tooling for
          production AI agents and co-authored SEGRA, a paper on
          experience-guided agents for graph question answering. Before that I
          built open-source GenAI applications at the UBC Cloud Innovation
          Centre with AWS, and today I lead UBC Launch Pad, UBC's largest
          student software engineering team.
          <br />
          <br />
          Outside of Computer Science I love to play basketball, hockey, lift
          weights, and listen to new music. Whether it’s on the ice or in front
          of a whiteboard debugging a tough problem, I embrace challenges with
          resilience and curiosity.
        </motion.p>
      </div>
      <motion.h2
        className="about-header"
        whileInView={{ opacity: 1, y: 0 }}
        initial={{ opacity: 0, y: 50 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        Skills
      </motion.h2>
      <div className="skills">
        <motion.div
          className="languages"
          whileInView={{ opacity: 1, y: 0 }}
          initial={{ opacity: 0, y: 50 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <h2 className="subheader">Languages</h2>
          <div className="languages-group relative flex-col grid grid-cols-3 md:flex md:flex-row items-center justify-center my-[10vh] gap-[4vw]">
            {languages.map((language, index) => (
              <div key={index}>
                <div className="skills-card" style={{}}>
                  <img
                    src={"/skillsimgs/" + language.name + ".png"}
                    alt={language.name}
                    className="skills-image"
                  />
                </div>
                <p className="skills-name">{language.name}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <div className="subgroup-one flex-col md:flex-row">
          <motion.div
            className="skills-group"
            whileInView={{ opacity: 1, x: 0 }}
            initial={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="subheader">Web Development</h2>
            <div className="web-dev">
              {webDevFrameworks.map((webdev, index) => (
                <div key={index}>
                  <div className="skills-card" style={{}}>
                    <img
                      src={"/skillsimgs/" + webdev + ".png"}
                      alt={webdev}
                      className="skills-image"
                    />
                  </div>
                  <p className="skills-name">{webdev}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="ml skills-group"
            whileInView={{ opacity: 1, x: 0 }}
            initial={{ opacity: 0, x: 50 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="subheader">Machine Learning &amp; AI</h2>
            <div className="ml-items">
              {mlframeworks.map(({ name, Icon, color }) => (
                <div key={name}>
                  <div className="skills-card">
                    {Icon ? (
                      <Icon size={38} color={color} aria-label={name} />
                    ) : (
                      <img
                        src={"/skillsimgs/" + name + ".png"}
                        alt={name}
                        className="skills-image"
                      />
                    )}
                  </div>
                  <p className="skills-name">{name}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
        <div className="subgroup-two flex-col md:flex-row">
          <motion.div
            className="tools skills-group"
            whileInView={{ opacity: 1, x: 0 }}
            initial={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="subheader">Dev Tools</h2>
            <div className="ml-items">
              {tools.map((tool, index) => (
                <div key={index}>
                  <div className="skills-card" style={{}}>
                    <img
                      src={"/skillsimgs/" + tool + ".png"}
                      alt={tool}
                      className="skills-image"
                    />
                  </div>
                  <p className="skills-name">{tool}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="testing skills-group"
            whileInView={{ opacity: 1, x: 0 }}
            initial={{ opacity: 0, x: 50 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="subheader">Testing</h2>
            <div className="web-dev">
              {testingframeworks.map((test, index) => (
                <div key={index}>
                  <div className="skills-card" style={{}}>
                    <img
                      src={"/skillsimgs/" + test + ".png"}
                      alt={test}
                      className="skills-image"
                    />
                  </div>
                  <p className="skills-name">{test}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default Home;
